import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Cookie, GraduationCap, Globe, ChevronRight, Mail, Phone, Building2, ShieldCheck, Mic } from 'lucide-react';
import { motion } from 'framer-motion';

const LegalPage = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('privacy');

  // Handle hash navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['privacy', 'terms', 'cookies', 'ferpa', 'gdpr', 'dpa', 'aup'].includes(hash)) {
      setActiveSection(hash);
    }
  }, [location.hash]);

  const sections = [
    { id: 'privacy', name: 'Privacy Policy', icon: Shield },
    { id: 'terms', name: 'Terms of Service', icon: FileText },
    { id: 'cookies', name: 'Cookie Policy', icon: Cookie },
    { id: 'aup', name: 'Acceptable Use Policy', icon: Mic },
    { id: 'ferpa', name: 'FERPA Compliance', icon: GraduationCap },
    { id: 'gdpr', name: 'GDPR', icon: Globe },
    { id: 'dpa', name: 'Data Processing Agreement', icon: Building2 },
  ];

  const lastUpdated = 'April 10, 2026';

  return (
    <div className="f500-page min-h-screen bg-[var(--isp-page)]" data-testid="legal-page">
      {/* Header */}
      <header className="border-b border-[var(--f500-line)] sticky top-0 bg-[var(--isp-page)] z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[var(--f500-ink-2)] hover:text-[var(--f500-ink)] transition-colors text-sm"
              style={{ fontFamily: 'var(--sf-text)' }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <span className="f500-eyebrow">Last updated · {lastUpdated}</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-[var(--isp-page)] border border-[var(--f500-line)] p-4">
              <p className="f500-eyebrow mb-4 px-2">— Legal Documents</p>
              <nav className="space-y-0">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const active = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                        active
                          ? 'bg-[var(--f500-ink)] text-white'
                          : 'text-[var(--f500-ink-2)] hover:text-[var(--f500-ink)] hover:bg-[var(--f500-paper-2)]'
                      }`}
                      style={{ fontFamily: 'var(--sf-text)' }}
                      data-testid={`legal-nav-${section.id}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                      <span className="text-sm font-medium">{section.name}</span>
                      {active && <ChevronRight className="w-4 h-4 ml-auto" />}
                    </button>
                  );
                })}
              </nav>
              
              {/* Contact */}
              <div className="mt-6 pt-4 border-t border-[var(--f500-line)]">
                <p className="f500-eyebrow px-2 mb-3">— Questions?</p>
                <a href="mailto:info@istudypro.net" className="flex items-center gap-2 text-[var(--f500-ink-2)] hover:text-[var(--f500-blue)] text-sm px-2 py-1 transition-colors" style={{ fontFamily: 'var(--sf-text)' }}>
                  <Mail className="w-3.5 h-3.5" strokeWidth={1.75} />
                  info@istudypro.net
                </a>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-[var(--isp-page)] border border-[var(--f500-line)] p-6 md:p-10"
            >
              {activeSection === 'privacy' && <PrivacyPolicy />}
              {activeSection === 'terms' && <TermsOfService />}
              {activeSection === 'cookies' && <CookiePolicy />}
              {activeSection === 'aup' && <AcceptableUsePolicy />}
              {activeSection === 'ferpa' && <FERPACompliance />}
              {activeSection === 'gdpr' && <GDPRCompliance />}
              {activeSection === 'dpa' && <DataProcessingAgreement />}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#E4E4E7] py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#A1A1AA] text-sm">
            © 2025 iStudyPro. All rights reserved. | <a href="https://www.istudypro.net" className="hover:text-[#0A0A0A] transition-colors">www.istudypro.net</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

// Privacy Policy Component
const PrivacyPolicy = () => (
  <div className="prose max-w-none f500-prose">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#F0F3FB] flex items-center justify-center">
        <Shield className="w-6 h-6 text-[#002FA7]" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A] m-0">Privacy Policy</h1>
        <p className="text-[#A1A1AA] text-sm m-0">Effective Date: January 30, 2025</p>
      </div>
    </div>

    <div className="space-y-6 text-[#52525B]">
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">1. Introduction</h2>
        <p>
          iStudyPro ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered educational platform and services (collectively, the "Service").
        </p>
        <p>
          By using iStudyPro, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access or use the Service.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">2. Information We Collect</h2>
        
        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">2.1 Personal Information</h3>
        <p>We may collect personally identifiable information, including but not limited to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Name and email address</li>
          <li>Educational institution and academic level</li>
          <li>Profile information and preferences</li>
          <li>Payment information (processed securely via third-party providers)</li>
          <li>Authentication data (including social login information)</li>
        </ul>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">2.2 Educational Content</h3>
        <p>When using our Service, we collect:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Lecture recordings and transcriptions</li>
          <li>Notes, flashcards, and study materials you create</li>
          <li>Quiz responses and learning progress data</li>
          <li>Study group interactions and shared content</li>
        </ul>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">2.3 Usage Data</h3>
        <p>We automatically collect certain information when you access the Service:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Device information (type, operating system, browser)</li>
          <li>IP address and approximate location</li>
          <li>Usage patterns and feature interactions</li>
          <li>Session duration and frequency of use</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">3. How We Use Your Information</h2>
        <p>We use the collected information for the following purposes:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Service Delivery:</strong> To provide, maintain, and improve our educational platform</li>
          <li><strong>Personalization:</strong> To customize your learning experience and provide AI-powered recommendations</li>
          <li><strong>Communication:</strong> To send you updates, notifications, and respond to inquiries</li>
          <li><strong>Analytics:</strong> To analyze usage patterns and improve our services</li>
          <li><strong>Security:</strong> To detect, prevent, and address technical issues and fraudulent activity</li>
          <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">4. Data Sharing and Disclosure</h2>
        <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Service Providers:</strong> With third-party vendors who assist in operating our Service (e.g., cloud hosting, payment processing, AI services)</li>
          <li><strong>Educational Institutions:</strong> With your school or university, if you access iStudyPro through an institutional license</li>
          <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
          <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of assets</li>
          <li><strong>With Your Consent:</strong> For any other purpose with your explicit consent</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">5. Data Security</h2>
        <p>
          We implement industry-standard security measures to protect your information, including:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Encryption of data in transit (TLS/SSL) and at rest</li>
          <li>Secure authentication mechanisms</li>
          <li>Regular security audits and vulnerability assessments</li>
          <li>Access controls and employee training</li>
        </ul>
        <p className="mt-3">
          While we strive to protect your information, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">6. Data Retention</h2>
        <p>
          We retain your personal information for as long as your account is active or as needed to provide you services. You may request deletion of your account and associated data at any time by contacting us at <a href="mailto:info@istudypro.net" className="text-[#002FA7] hover:text-purple-300">info@istudypro.net</a>.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">7. Your Rights</h2>
        <p>Depending on your location, you may have the following rights:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Access and receive a copy of your personal data</li>
          <li>Rectify inaccurate or incomplete information</li>
          <li>Request deletion of your personal data</li>
          <li>Object to or restrict processing of your data</li>
          <li>Data portability</li>
          <li>Withdraw consent at any time</li>
        </ul>
        <p className="mt-3">To exercise these rights, please contact us at <a href="mailto:info@istudypro.net" className="text-[#002FA7] hover:text-purple-300">info@istudypro.net</a>.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">8. Children's Privacy</h2>
        <p>
          iStudyPro is intended for users aged 13 and older. We do not knowingly collect personal information from children under 13. If we become aware that we have collected data from a child under 13, we will take steps to delete such information promptly.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">9. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date." Your continued use of the Service after any changes constitutes acceptance of the updated policy.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">10. Contact Us</h2>
        <p>If you have any questions about this Privacy Policy, please contact us:</p>
        <div className="bg-white/5 rounded-xl p-4 mt-3">
          <p className="m-0"><strong>iStudyPro</strong></p>
          <p className="m-0">Email: <a href="mailto:info@istudypro.net" className="text-[#002FA7] hover:text-purple-300">info@istudypro.net</a></p>
          <p className="m-0">Phone: <a href="tel:+233246257382" className="text-[#002FA7] hover:text-purple-300">+233 (024) 625-7382</a></p>
          <p className="m-0">Website: <a href="https://www.istudypro.net" className="text-[#002FA7] hover:text-purple-300">www.istudypro.net</a></p>
        </div>
      </section>
    </div>
  </div>
);

// Terms of Service Component
const TermsOfService = () => (
  <div className="prose max-w-none f500-prose">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#F0F3FB] flex items-center justify-center">
        <FileText className="w-6 h-6 text-[#002FA7]" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A] m-0">Terms of Service</h1>
        <p className="text-[#A1A1AA] text-sm m-0">Effective Date: January 30, 2025</p>
      </div>
    </div>

    <div className="space-y-6 text-[#52525B]">
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">1. Acceptance of Terms</h2>
        <p>
          By accessing or using iStudyPro ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these Terms, you may not access the Service.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">2. Description of Service</h2>
        <p>
          iStudyPro is an AI-powered educational platform that provides:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Lecture recording and transcription services</li>
          <li>AI-generated flashcards, quizzes, and study materials</li>
          <li>Personalized AI tutoring and study recommendations</li>
          <li>Collaborative study groups and resource sharing</li>
          <li>Progress tracking and gamification features</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">3. User Accounts</h2>
        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">3.1 Registration</h3>
        <p>
          To use certain features of the Service, you must register for an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.
        </p>
        
        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">3.2 Account Security</h3>
        <p>
          You are responsible for safeguarding your password and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
        </p>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">3.3 Age Requirement</h3>
        <p>
          You must be at least 13 years old to use the Service. If you are under 18, you represent that you have your parent's or guardian's permission to use the Service.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">4. Acceptable Use</h2>
        <p>You agree NOT to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Use the Service for any unlawful purpose or in violation of any laws</li>
          <li>Upload content that infringes intellectual property rights</li>
          <li>Share account credentials or allow others to access your account</li>
          <li>Attempt to gain unauthorized access to the Service or its systems</li>
          <li>Use the Service to harass, abuse, or harm others</li>
          <li>Upload malicious code, viruses, or harmful content</li>
          <li>Use automated systems to access the Service without permission</li>
          <li>Resell or redistribute the Service without authorization</li>
          <li>Use AI-generated content to misrepresent academic work as your own original work when prohibited by your institution</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">5. Intellectual Property</h2>
        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">5.1 Our Content</h3>
        <p>
          The Service and its original content, features, and functionality are owned by iStudyPro and are protected by international copyright, trademark, and other intellectual property laws.
        </p>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">5.2 Your Content</h3>
        <p>
          You retain ownership of content you upload to the Service. By uploading content, you grant us a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, and display such content solely for the purpose of providing and improving the Service.
        </p>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">5.3 AI-Generated Content</h3>
        <p>
          Content generated by our AI features (such as flashcards, summaries, and quiz questions) is provided for educational purposes. You may use this content for personal study but should comply with your institution's academic integrity policies.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">6. Subscription and Payment</h2>
        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">6.1 Pricing</h3>
        <p>
          Some features of the Service require a paid subscription. Prices are displayed in your local currency and may be subject to change with notice.
        </p>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">6.2 Billing</h3>
        <p>
          Subscription fees are billed in advance on a monthly or annual basis. Your subscription will automatically renew unless you cancel before the renewal date.
        </p>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">6.3 Refunds</h3>
        <p>
          Refunds are handled on a case-by-case basis. Please contact us at <a href="mailto:info@istudypro.net" className="text-[#002FA7] hover:text-cyan-300">info@istudypro.net</a> for refund requests.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">7. Disclaimers</h2>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
        </p>
        <p className="mt-3">
          AI-generated content is provided for educational assistance only and should not be considered a substitute for professional academic guidance or your own critical thinking.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">8. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, ISTUDYPRO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, ACADEMIC PERFORMANCE, OR PROFITS.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">9. Termination</h2>
        <p>
          We may terminate or suspend your account immediately, without prior notice, for any breach of these Terms. Upon termination, your right to use the Service will cease immediately. You may also delete your account at any time through your account settings.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">10. Governing Law</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of Ghana, without regard to its conflict of law provisions.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">11. Changes to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting a notice on the Service. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">12. Contact</h2>
        <p>For questions about these Terms, please contact us at <a href="mailto:info@istudypro.net" className="text-[#002FA7] hover:text-cyan-300">info@istudypro.net</a>.</p>
      </section>
    </div>
  </div>
);

// Cookie Policy Component
const CookiePolicy = () => (
  <div className="prose max-w-none f500-prose">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#FFF8EC] flex items-center justify-center">
        <Cookie className="w-6 h-6 text-[#F59E0B]" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A] m-0">Cookie Policy</h1>
        <p className="text-[#A1A1AA] text-sm m-0">Effective Date: January 30, 2025</p>
      </div>
    </div>

    <div className="space-y-6 text-[#52525B]">
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">1. What Are Cookies?</h2>
        <p>
          Cookies are small text files that are stored on your device when you visit a website. They help websites remember your preferences and improve your browsing experience.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">2. How We Use Cookies</h2>
        <p>iStudyPro uses cookies for the following purposes:</p>
        
        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">2.1 Essential Cookies</h3>
        <p>These cookies are necessary for the Service to function properly:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Authentication:</strong> To keep you logged in during your session</li>
          <li><strong>Security:</strong> To protect against fraudulent activity</li>
          <li><strong>Session Management:</strong> To remember your preferences within a session</li>
        </ul>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">2.2 Functional Cookies</h3>
        <p>These cookies enhance your experience:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Language Preferences:</strong> To remember your selected language</li>
          <li><strong>Theme Settings:</strong> To remember your dark/light mode preference</li>
          <li><strong>User Preferences:</strong> To store your notification and display settings</li>
        </ul>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">2.3 Analytics Cookies</h3>
        <p>These cookies help us understand how you use our Service:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Usage Analytics:</strong> To analyze how users interact with features</li>
          <li><strong>Performance Monitoring:</strong> To identify and fix technical issues</li>
          <li><strong>Feature Adoption:</strong> To understand which features are most valuable</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">3. Cookies We Use</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-2 text-[#0A0A0A]">Cookie Name</th>
                <th className="text-left py-3 px-2 text-[#0A0A0A]">Purpose</th>
                <th className="text-left py-3 px-2 text-[#0A0A0A]">Duration</th>
              </tr>
            </thead>
            <tbody className="text-[#52525B]">
              <tr className="border-b border-white/5">
                <td className="py-3 px-2">session_token</td>
                <td className="py-3 px-2">Authentication</td>
                <td className="py-3 px-2">7 days</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-2">token</td>
                <td className="py-3 px-2">JWT Authentication</td>
                <td className="py-3 px-2">7 days</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-2">theme</td>
                <td className="py-3 px-2">Theme preference</td>
                <td className="py-3 px-2">1 year</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-2">i18nextLng</td>
                <td className="py-3 px-2">Language preference</td>
                <td className="py-3 px-2">1 year</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-2">istudypro_email</td>
                <td className="py-3 px-2">Remember email for login</td>
                <td className="py-3 px-2">30 days</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">4. Third-Party Cookies</h2>
        <p>We may use third-party services that set their own cookies:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Google OAuth:</strong> For social login functionality</li>
          <li><strong>Paystack:</strong> For payment processing</li>
          <li><strong>Analytics Services:</strong> For usage analytics</li>
        </ul>
        <p className="mt-3">
          These third parties have their own privacy policies governing the use of cookies.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">5. Managing Cookies</h2>
        <p>You can control cookies through your browser settings:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
          <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies</li>
          <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
          <li><strong>Edge:</strong> Settings → Privacy & Services → Cookies</li>
        </ul>
        <p className="mt-3">
          <strong>Note:</strong> Disabling essential cookies may prevent you from using certain features of the Service.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">6. Updates to This Policy</h2>
        <p>
          We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated effective date.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">7. Contact Us</h2>
        <p>
          For questions about our use of cookies, please contact us at <a href="mailto:info@istudypro.net" className="text-[#F59E0B] hover:text-amber-300">info@istudypro.net</a>.
        </p>
      </section>
    </div>
  </div>
);

// FERPA Compliance Component
const FERPACompliance = () => (
  <div className="prose max-w-none f500-prose">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#F4FAF6] flex items-center justify-center">
        <GraduationCap className="w-6 h-6 text-[#34C759]" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A] m-0">FERPA Compliance</h1>
        <p className="text-[#A1A1AA] text-sm m-0">Family Educational Rights and Privacy Act</p>
      </div>
    </div>

    <div className="space-y-6 text-[#52525B]">
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">1. Our Commitment to FERPA</h2>
        <p>
          iStudyPro is committed to complying with the Family Educational Rights and Privacy Act (FERPA) when working with educational institutions. FERPA protects the privacy of student education records and gives parents and eligible students certain rights regarding those records.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">2. What is FERPA?</h2>
        <p>
          FERPA is a federal law that protects the privacy of student education records. It applies to all schools that receive funds from applicable programs of the U.S. Department of Education. Under FERPA:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Parents have rights to access their children's education records (for students under 18)</li>
          <li>Students 18 and older have rights to access their own records</li>
          <li>Schools must have written permission to release student records</li>
          <li>Schools must notify parents/students of their rights annually</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">3. iStudyPro as a "School Official"</h2>
        <p>
          When iStudyPro contracts with educational institutions, we may act as a "school official" under FERPA. In this capacity, we:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Use education records only for authorized purposes</li>
          <li>Maintain the confidentiality of student information</li>
          <li>Do not disclose education records without proper consent</li>
          <li>Follow the institution's policies regarding student data</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">4. Education Records We May Handle</h2>
        <p>Through institutional partnerships, we may process:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Student names and email addresses</li>
          <li>Course enrollment information</li>
          <li>Academic content created by students (notes, assignments)</li>
          <li>Learning progress and assessment data</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">5. Our FERPA Safeguards</h2>
        <p>To ensure FERPA compliance, iStudyPro implements:</p>
        
        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">5.1 Administrative Safeguards</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Written agreements with educational institutions</li>
          <li>Staff training on FERPA requirements</li>
          <li>Policies limiting access to education records</li>
          <li>Regular compliance audits</li>
        </ul>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">5.2 Technical Safeguards</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Encryption of data in transit and at rest</li>
          <li>Role-based access controls</li>
          <li>Audit logging of data access</li>
          <li>Secure authentication mechanisms</li>
        </ul>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">5.3 Physical Safeguards</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Secure data center facilities</li>
          <li>Access controls to physical infrastructure</li>
          <li>Environmental protections</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">6. Student and Parent Rights</h2>
        <p>Under FERPA, eligible students and parents have the right to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Inspect and Review:</strong> Access education records maintained by the institution</li>
          <li><strong>Request Amendment:</strong> Request correction of inaccurate or misleading records</li>
          <li><strong>Consent to Disclosure:</strong> Control disclosure of personally identifiable information</li>
          <li><strong>File Complaints:</strong> File complaints with the U.S. Department of Education</li>
        </ul>
        <p className="mt-3">
          To exercise these rights, please contact your educational institution directly.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">7. Data Breach Response</h2>
        <p>
          In the event of a data breach involving education records, iStudyPro will:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Immediately notify the affected educational institution</li>
          <li>Cooperate with breach investigation and remediation</li>
          <li>Assist with required notifications to affected individuals</li>
          <li>Implement measures to prevent future breaches</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">8. For Educational Institutions</h2>
        <p>
          If you are an educational institution interested in using iStudyPro, we can provide:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Data Processing Agreements (DPA)</li>
          <li>FERPA compliance documentation</li>
          <li>Security assessment questionnaires</li>
          <li>Custom data retention policies</li>
        </ul>
        <p className="mt-3">
          Contact us at <a href="mailto:info@istudypro.net" className="text-[#34C759] hover:text-green-300">info@istudypro.net</a> for institutional partnership inquiries.
        </p>
      </section>
    </div>
  </div>
);

// GDPR Compliance Component
const GDPRCompliance = () => (
  <div className="prose max-w-none f500-prose">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-[#F0F3FB] flex items-center justify-center">
        <Globe className="w-6 h-6 text-[#002FA7]" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A] m-0">GDPR Compliance</h1>
        <p className="text-[#A1A1AA] text-sm m-0">General Data Protection Regulation</p>
      </div>
    </div>

    <div className="space-y-6 text-[#52525B]">
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">1. Our Commitment to GDPR</h2>
        <p>
          iStudyPro is committed to protecting the personal data of users in the European Union (EU) and European Economic Area (EEA) in compliance with the General Data Protection Regulation (GDPR).
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">2. Legal Basis for Processing</h2>
        <p>We process personal data under the following legal bases:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Contract Performance:</strong> To provide the Service you have requested</li>
          <li><strong>Legitimate Interests:</strong> To improve our services and ensure security</li>
          <li><strong>Consent:</strong> For marketing communications and optional features</li>
          <li><strong>Legal Obligation:</strong> To comply with applicable laws</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">3. Your GDPR Rights</h2>
        <p>If you are in the EU/EEA, you have the following rights under GDPR:</p>
        
        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">3.1 Right of Access (Article 15)</h3>
        <p>You have the right to obtain confirmation of whether we process your personal data and to receive a copy of that data.</p>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">3.2 Right to Rectification (Article 16)</h3>
        <p>You have the right to request correction of inaccurate personal data and completion of incomplete data.</p>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">3.3 Right to Erasure (Article 17)</h3>
        <p>You have the right to request deletion of your personal data ("right to be forgotten") in certain circumstances.</p>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">3.4 Right to Restriction (Article 18)</h3>
        <p>You have the right to request restriction of processing of your personal data in certain circumstances.</p>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">3.5 Right to Data Portability (Article 20)</h3>
        <p>You have the right to receive your personal data in a structured, commonly used, machine-readable format.</p>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">3.6 Right to Object (Article 21)</h3>
        <p>You have the right to object to processing based on legitimate interests or for direct marketing purposes.</p>

        <h3 className="text-lg font-medium text-[#0A0A0A] mt-4 mb-2">3.7 Right to Withdraw Consent (Article 7)</h3>
        <p>Where processing is based on consent, you have the right to withdraw consent at any time.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">4. Exercising Your Rights</h2>
        <p>To exercise any of these rights, please contact us:</p>
        <div className="bg-white/5 rounded-xl p-4 mt-3">
          <p className="m-0"><strong>Data Protection Contact</strong></p>
          <p className="m-0">Email: <a href="mailto:info@istudypro.net" className="text-[#002FA7] hover:text-blue-300">info@istudypro.net</a></p>
          <p className="m-0">Subject: "GDPR Data Request"</p>
        </div>
        <p className="mt-3">
          We will respond to your request within 30 days. We may request additional information to verify your identity before processing your request.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">5. Data Transfers</h2>
        <p>
          Your personal data may be transferred to and processed in countries outside the EU/EEA. When we transfer data internationally, we ensure appropriate safeguards are in place:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
          <li>Transfers to countries with adequacy decisions</li>
          <li>Other appropriate safeguards as required by GDPR</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">6. Data Retention</h2>
        <p>
          We retain personal data only for as long as necessary to fulfill the purposes for which it was collected, unless a longer retention period is required by law. When you delete your account, we will delete or anonymize your personal data within 30 days, except where we need to retain certain data for legal or legitimate business purposes.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">7. Data Protection Measures</h2>
        <p>We implement appropriate technical and organizational measures including:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Encryption of personal data in transit and at rest</li>
          <li>Regular security assessments and penetration testing</li>
          <li>Access controls and authentication mechanisms</li>
          <li>Staff training on data protection</li>
          <li>Incident response procedures</li>
          <li>Data processing agreements with sub-processors</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">8. Data Breach Notification</h2>
        <p>
          In the event of a personal data breach that is likely to result in a risk to your rights and freedoms, we will notify the relevant supervisory authority within 72 hours where feasible. If the breach is likely to result in a high risk to your rights, we will also notify you directly.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">9. Supervisory Authority</h2>
        <p>
          If you are not satisfied with our response to your data protection concerns, you have the right to lodge a complaint with your local supervisory authority. A list of EU supervisory authorities can be found on the European Data Protection Board website.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">10. Contact Information</h2>
        <p>For any GDPR-related inquiries, please contact:</p>
        <div className="bg-white/5 rounded-xl p-4 mt-3">
          <p className="m-0"><strong>iStudyPro Data Protection</strong></p>
          <p className="m-0">Email: <a href="mailto:info@istudypro.net" className="text-[#002FA7] hover:text-blue-300">info@istudypro.net</a></p>
          <p className="m-0">Phone: <a href="tel:+233246257382" className="text-[#002FA7] hover:text-blue-300">+233 (024) 625-7382</a></p>
          <p className="m-0">Website: <a href="https://www.istudypro.net" className="text-[#002FA7] hover:text-blue-300">www.istudypro.net</a></p>
        </div>
      </section>
    </div>
  </div>
);

// Acceptable Use Policy Component
const AcceptableUsePolicy = () => (
  <div className="prose max-w-none f500-prose">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center">
        <Mic className="w-6 h-6 text-pink-400" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A] m-0">Acceptable Use Policy</h1>
        <p className="text-[#A1A1AA] text-sm m-0">Recording Guidelines for Students & Institutions</p>
      </div>
    </div>

    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6 flex items-start gap-3">
      <ShieldCheck className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" />
      <p className="text-sm text-amber-300 m-0">
        iStudyPRO is designed to help students learn more effectively. By using our recording and AI features, you agree to abide by these guidelines. Violations may result in account suspension.
      </p>
    </div>

    <div className="space-y-6 text-[#52525B]">
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">1. What You CAN Record</h2>
        <div className="space-y-2">
          {[
            { title: 'Your own lectures and classes', desc: 'Record any class you are enrolled in, with the knowledge and consent of the instructor where required by your institution.' },
            { title: 'Study group discussions', desc: 'Record collaborative study sessions with the verbal consent of all participants.' },
            { title: 'Personal study notes', desc: 'Use the app to dictate and transcribe your own thoughts, revision notes, and exam preparation material.' },
            { title: 'Public academic events', desc: 'Record public lectures, seminars, and workshops that are open to attendees.' },
          ].map((item, i) => (
            <div key={`right-${i}`} className="bg-emerald-500/5 border border-emerald-500/15 rounded-xl p-4">
              <h3 className="text-base font-medium text-emerald-400 m-0">{item.title}</h3>
              <p className="text-sm m-0 mt-1">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">2. What You CANNOT Record</h2>
        <div className="space-y-2">
          {[
            { title: 'Private conversations without consent', desc: 'Never record private conversations, meetings, or interactions where participants have not given consent. This may violate Ghana\'s Electronic Transactions Act and wiretapping laws.' },
            { title: 'Exam and assessment content', desc: 'Do not record, transcribe, or share exam questions, assessment answers, or any content that would constitute academic dishonesty.' },
            { title: 'Copyrighted material for redistribution', desc: 'You may not use iStudyPRO to transcribe copyrighted lectures, audiobooks, or media for the purpose of redistribution or commercial use.' },
            { title: 'Content involving minors', desc: 'Do not record children under 18 without explicit parental or guardian consent.' },
            { title: 'Hateful, harmful, or illegal content', desc: 'iStudyPRO must not be used to process, store, or generate content that promotes violence, harassment, discrimination, or any illegal activity.' },
          ].map((item, i) => (
            <div key={`prohib-${i}`} className="bg-red-500/5 border border-red-500/15 rounded-xl p-4">
              <h3 className="text-base font-medium text-[#FF3B30] m-0">{item.title}</h3>
              <p className="text-sm m-0 mt-1">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">3. Instructor & Institutional Consent</h2>
        <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-3">
          <p className="m-0"><strong className="text-[#0A0A0A]">Check Your Institution's Policy:</strong> Many universities have specific rules about lecture recording. Before recording, verify whether your institution requires:</p>
          <ul className="list-disc pl-6 space-y-1 m-0">
            <li>Written permission from the lecturer</li>
            <li>Notification at the start of each recorded session</li>
            <li>Restrictions on sharing transcripts with non-enrolled students</li>
          </ul>
          <p className="m-0"><strong className="text-[#0A0A0A]">Our Recommendation:</strong> Always inform your lecturer that you are recording for personal study purposes. Most educators welcome it when approached respectfully.</p>
          <p className="m-0"><strong className="text-[#0A0A0A]">Campus Plan Institutions:</strong> If your university uses iStudyPRO's Campus Plan, recording policies are configured by your institution's IT administrator. Contact your campus admin for specific guidelines.</p>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">4. AI-Generated Content Guidelines</h2>
        <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-3">
          <p className="m-0"><strong className="text-[#0A0A0A]">Accuracy Disclaimer:</strong> AI-generated summaries, flashcards, and study plans are tools to assist your learning. They are <strong className="text-[#F59E0B]">not a substitute for your own understanding</strong>. Always verify AI outputs against your original lecture notes and course materials.</p>
          <p className="m-0"><strong className="text-[#0A0A0A]">Academic Integrity:</strong> Submitting AI-generated content as your own original work may violate your institution's academic integrity policy. Use iStudyPRO as a <strong className="text-emerald-400">study aid</strong>, not a ghostwriter.</p>
          <p className="m-0"><strong className="text-[#0A0A0A]">Right to Human Review:</strong> If you believe an AI-graded quiz or exam result is incorrect, you have the right to request a human review via Settings &gt; Transparency Center.</p>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">5. Data Sharing & Collaboration</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong className="text-[#0A0A0A]">Study Groups:</strong> When sharing transcripts or summaries in Study Groups, ensure all group members consent to receiving AI-processed content from your recordings.</li>
          <li><strong className="text-[#0A0A0A]">No Public Redistribution:</strong> Transcripts and summaries generated from lectures are for personal and group study use only. Do not publish them on social media, file-sharing platforms, or public websites.</li>
          <li><strong className="text-[#0A0A0A]">Sensitive Topics:</strong> Be mindful when sharing notes from classes covering sensitive topics (medical case studies, legal proceedings, etc.). Redact any personally identifiable information before sharing.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">6. Storage & Retention</h2>
        <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-3">
          <p className="m-0"><strong className="text-[#0A0A0A]">Your Responsibility:</strong> You are responsible for managing your stored recordings and transcripts. We recommend:</p>
          <ul className="list-disc pl-6 space-y-1 m-0">
            <li>Regularly reviewing and deleting recordings you no longer need</li>
            <li>Downloading important transcripts for offline backup</li>
            <li>Using the "Download My Brain" feature before graduation to export all your study materials</li>
          </ul>
          <p className="m-0"><strong className="text-[#0A0A0A]">Storage Limits:</strong> Free accounts receive 512MB of storage. Student and Pro plans receive 10GB and 100GB respectively. Audio is automatically compressed to Opus format (~84% size reduction) to maximize your available space.</p>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">7. Enforcement</h2>
        <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-3">
          <p className="m-0">Violations of this Acceptable Use Policy may result in:</p>
          <ul className="list-disc pl-6 space-y-1 m-0">
            <li><strong className="text-[#F59E0B]">Warning:</strong> First-time minor violations will receive a written notice</li>
            <li><strong className="text-orange-400">Temporary Suspension:</strong> Repeated violations or moderate offenses may result in 7-30 day account suspension</li>
            <li><strong className="text-[#FF3B30]">Permanent Ban:</strong> Severe violations (academic fraud, illegal recordings, distribution of copyrighted content) will result in immediate and permanent account termination</li>
          </ul>
          <p className="m-0 mt-3">To report a potential AUP violation, contact <a href="mailto:info@istudypro.net" className="text-emerald-400 hover:text-emerald-300">info@istudypro.net</a>.</p>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">8. Ghana-Specific Recording Laws</h2>
        <div className="bg-white/5 rounded-xl p-5 border border-white/10 space-y-3">
          <p className="m-0"><strong className="text-[#0A0A0A]">Electronic Transactions Act, 2008 (Act 772):</strong> Ghana's electronic transactions law governs the admissibility and handling of electronic records. Recordings made through iStudyPRO are considered electronic records.</p>
          <p className="m-0"><strong className="text-[#0A0A0A]">One-Party Consent:</strong> Ghana generally follows a one-party consent framework for recordings. However, we strongly recommend informing all parties as a matter of professional courtesy and institutional policy compliance.</p>
          <p className="m-0"><strong className="text-[#0A0A0A]">Data Protection Act, 2012 (Act 843):</strong> If your recordings contain personally identifiable information of other individuals, you bear responsibility as a data controller for that content under the DPA. iStudyPRO provides the technical infrastructure; you control the content.</p>
        </div>
      </section>
    </div>
  </div>
);


// Data Processing Agreement Component
const DataProcessingAgreement = () => (
  <div className="prose max-w-none f500-prose">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
        <Building2 className="w-6 h-6 text-emerald-400" />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A] m-0">Data Processing Addendum (Summary)</h1>
        <p className="text-[#A1A1AA] text-sm m-0">Compliance with Ghana DPA 2012 (Act 843), GDPR, and AI Privacy Standards (2026)</p>
      </div>
    </div>

    {/* Trust Badge */}
    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 mb-6 flex items-start gap-3">
      <ShieldCheck className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
      <p className="text-sm text-emerald-300 m-0">
        This document is provided for University IT Departments, Compliance Officers, and Institutional Partners to verify iStudyPRO's data handling practices. Our systems are built to meet Ghana DPA 2012 and GDPR standards, and we are actively processing our formal certification with the Data Protection Commission. A full legally-binding DPA is available upon request.
      </p>
    </div>

    <div className="space-y-6 text-[#52525B]">
      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">1. Scope & Purpose of Processing</h2>
        <div className="bg-white/5 rounded-xl p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-[#A1A1AA]">Data Controller:</span>
            <span className="text-white font-medium">The Educational Institution or Individual Student</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#A1A1AA]">Data Processor:</span>
            <span className="text-white font-medium">iStudyPRO (istudypro.net)</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#A1A1AA]">Designated DPS:</span>
            <span className="text-white font-medium">Certified Data Protection Supervisor (Section 27, Act 843)</span>
          </div>
        </div>
        <p className="mt-3">
          <strong className="text-[#0A0A0A]">Purpose:</strong> To provide AI-driven transcription, summarization, and study-aid generation. Data is processed <strong className="text-emerald-400">solely</strong> for the benefit of the user and is never sold or shared with third-party advertisers.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">2. Technical & Organizational Measures (TOMs)</h2>
        <p>We implement "Privacy by Design" via the following controls:</p>
        
        <div className="grid gap-3 mt-4">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <h3 className="text-lg font-medium text-[#0A0A0A] m-0 mb-2">Encryption</h3>
            <p className="m-0 text-sm">All student data is encrypted at rest using <strong className="text-emerald-400">AES-256</strong> and in transit via <strong className="text-emerald-400">TLS 1.3</strong>. Database connections use authenticated SSL certificates.</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <h3 className="text-lg font-medium text-[#0A0A0A] m-0 mb-2">Zero-Knowledge Architecture</h3>
            <p className="m-0 text-sm">For users in <strong className="text-emerald-400">"Local Privacy Mode,"</strong> audio is processed on-device via WebSpeech API; iStudyPRO servers never receive the raw audio. In Cloud Mode, audio is compressed, encrypted, and permanently deleted after transcription (zero-retention buffer).</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <h3 className="text-lg font-medium text-[#0A0A0A] m-0 mb-2">Access Control</h3>
            <p className="m-0 text-sm">JWT-scoped authentication with Role-Based Access Control (RBAC). Session tokens are bound to specific user IDs, preventing IDOR (Insecure Direct Object Reference) vulnerabilities.</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <h3 className="text-lg font-medium text-[#0A0A0A] m-0 mb-2">Audio Compression & Storage Optimization</h3>
            <p className="m-0 text-sm">Audio recordings are compressed to <strong className="text-emerald-400">OGG/Opus at 32k bitrate</strong> (~84% size reduction). MD5 hash-based deduplication prevents storing identical files. Per-user storage quotas enforce tier-based limits (Free: 0.5GB, Student: 10GB, Pro: 100GB).</p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">3. Sub-Processor Management</h2>
        <p>iStudyPRO uses vetted sub-processors bound by strict <strong className="text-[#0A0A0A]">"No-Training"</strong> clauses:</p>
        
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-3 text-[#0A0A0A]">Sub-Processor</th>
                <th className="text-left py-3 px-3 text-[#0A0A0A]">Purpose</th>
                <th className="text-left py-3 px-3 text-[#0A0A0A]">Data Handled</th>
                <th className="text-left py-3 px-3 text-[#0A0A0A]">No-Train</th>
              </tr>
            </thead>
            <tbody className="text-[#52525B]">
              <tr className="border-b border-white/5">
                <td className="py-3 px-3">Groq (Whisper)</td>
                <td className="py-3 px-3">Audio Transcription</td>
                <td className="py-3 px-3">Compressed audio (ephemeral)</td>
                <td className="py-3 px-3"><span className="text-emerald-400">X-No-Train: true</span></td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-3">Google (Gemini)</td>
                <td className="py-3 px-3">AI Summarization & Study Tools</td>
                <td className="py-3 px-3">Text content only</td>
                <td className="py-3 px-3"><span className="text-emerald-400">X-No-Train: true</span></td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-3">Paystack</td>
                <td className="py-3 px-3">Payment Processing</td>
                <td className="py-3 px-3">Payment tokens (no card data stored)</td>
                <td className="py-3 px-3">N/A (PCI-DSS)</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="py-3 px-3">Emergent.sh</td>
                <td className="py-3 px-3">Infrastructure & Object Storage</td>
                <td className="py-3 px-3">Encrypted files</td>
                <td className="py-3 px-3"><span className="text-emerald-400">Compliant</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <p className="mt-3">
          <strong className="text-[#0A0A0A]">No Data Mining:</strong> We contractually prohibit our AI vendors from using student data to train their foundational models. All API calls include <code className="text-emerald-400">X-No-Train</code> headers where supported.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">4. Data Subject Rights (Ghana DPA 2012 & GDPR)</h2>
        <p>We provide <strong className="text-[#0A0A0A]">built-in tools</strong> for users to exercise their legal rights directly within the application:</p>
        
        <div className="grid gap-3 mt-4">
          <div className="bg-white/5 rounded-xl p-4 border-l-4 border-emerald-500">
            <h3 className="text-base font-medium text-[#0A0A0A] m-0">Right to Erasure (Art. 17)</h3>
            <p className="m-0 text-sm mt-1">Users can trigger a "Hard Delete" which purges all data from our active systems and backups within 30 days. Available in Settings &gt; Transparency Center.</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border-l-4 border-blue-500">
            <h3 className="text-base font-medium text-[#0A0A0A] m-0">Right to Portability (Art. 20)</h3>
            <p className="m-0 text-sm mt-1">Students can export their full study library in JSON format at any time via "Download My Brain" in Settings. Includes: lectures, flashcards, chat sessions, exam results, and processing logs.</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border-l-4 border-purple-500">
            <h3 className="text-base font-medium text-[#0A0A0A] m-0">Right to Human Review (Art. 22)</h3>
            <p className="m-0 text-sm mt-1">If our AI makes a grading decision (e.g., exam scoring), the student can request a human review directly within the platform.</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border-l-4 border-amber-500">
            <h3 className="text-base font-medium text-[#0A0A0A] m-0">Granular Consent Management</h3>
            <p className="m-0 text-sm mt-1">Explicit, revocable consent toggles for: Audio Recording, AI Summarization, Usage Analytics, and Marketing communications. Full audit trail maintained.</p>
          </div>
        </div>

        <div className="bg-white/5 rounded-xl p-4 mt-4 border border-white/10">
          <h3 className="text-base font-medium text-[#0A0A0A] m-0">Data Breach Notification</h3>
          <p className="m-0 text-sm mt-1">
            In the event of a suspected breach, iStudyPRO commits to notifying the <strong className="text-[#0A0A0A]">Ghana Data Protection Commission (DPC)</strong> and affected users within <strong className="text-emerald-400">72 hours</strong>, in compliance with GDPR Art. 33 and Ghana DPA 2012.
          </p>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">5. Data Residency & Transfer</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong className="text-[#0A0A0A]">Ghanaian Users:</strong> We prioritize data residency within local or regional data centers where possible to align with DPC 2026 enforcement guidelines.</li>
          <li><strong className="text-[#0A0A0A]">EU/Global Users:</strong> Standard Contractual Clauses (SCCs) approved by the European Commission are in place to ensure a high level of protection during cross-border transfers.</li>
          <li><strong className="text-[#0A0A0A]">Data Minimization:</strong> Only essential data is transmitted to sub-processors. Audio files are deleted immediately after transcription (zero-retention processing).</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">6. Processing Activity Register</h2>
        <p>iStudyPRO maintains a live, transparent processing log accessible to each user via Settings &gt; Transparency Center. Every data processing event is recorded with:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Action performed (transcription, AI summarization, data export, etc.)</li>
          <li>Purpose of processing</li>
          <li>Data type (audio, text, metadata)</li>
          <li>Retention policy (ephemeral vs. stored)</li>
          <li>Third-party involvement and No-Train header status</li>
          <li>Timestamp</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">7. Audit & Accountability</h2>
        <div className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-3">
          <p className="m-0"><strong className="text-[#0A0A0A]">Right to Audit:</strong> Educational institutions may request an audit of our data processing practices, security controls, and compliance measures. We are confident in our protocols and welcome institutional review.</p>
          <p className="m-0"><strong className="text-[#0A0A0A]">DPC Registration:</strong> iStudyPRO is actively processing its registration as a Data Controller with the Ghana Data Protection Commission as required by the Data Protection Act 2012 (Act 843). Our systems are designed and built to meet all DPA 2012 requirements ahead of formal certification.</p>
          <p className="m-0"><strong className="text-[#0A0A0A]">CDPS Designation:</strong> A Data Protection Supervisor has been designated to oversee internal audits, compliance training, and regulatory correspondence per Section 27 of Act 843, supporting our ongoing certification process.</p>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0A0A0A] mb-3">8. For University IT Departments</h2>
        <p>To facilitate institutional onboarding, we provide:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Full legally-binding Data Processing Agreement (available on request)</li>
          <li>Security Assessment Questionnaire (SAQ) responses</li>
          <li>SOC 2 Type II readiness documentation</li>
          <li>Custom data retention policies per institutional requirements</li>
          <li>Campus SSO integration (SAML 2.0 / Okta / Azure AD)</li>
          <li>Dedicated compliance contact for escalations</li>
        </ul>

        <div className="bg-emerald-500/10 rounded-xl p-4 mt-4 border border-emerald-500/20">
          <p className="m-0 text-sm"><strong className="text-emerald-400">Contact for Institutional Partnerships:</strong></p>
          <p className="m-0 text-sm mt-1">Email: <a href="mailto:info@istudypro.net" className="text-emerald-400 hover:text-emerald-300">info@istudypro.net</a></p>
          <p className="m-0 text-sm">Phone: <a href="tel:+233246257382" className="text-emerald-400 hover:text-emerald-300">+233 (024) 625-7382</a></p>
          <p className="m-0 text-sm">Subject: "DPA / Institutional Partnership Inquiry"</p>
        </div>
      </section>
    </div>
  </div>
);


export default LegalPage;
